import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import type { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

export type IGpthelpHiddenReason =
  | 'disabled' // visible === false
  | 'hidden_in_branch' // hiddenBranchesIds includes selected branch id (as string)
  | 'outside_hours' // outside hourRanges now (only when ctx.hoursEnforced)
  | 'pos_only'; // categories only: category.posOnly

export type IGpthelpItemHiddenReason = Exclude<
  IGpthelpHiddenReason,
  'pos_only'
>;

export type IGpthelpResolvedHiddenReason =
  | 'item_disabled'
  | 'item_hidden_in_branch'
  | 'item_outside_hours'
  | 'category_disabled'
  | 'category_hidden_in_branch'
  | 'category_pos_only'
  | 'category_outside_hours'
  | 'menu_not_offered' // category.menuId maps to no available menu
  | 'category_missing'; // item's categoryId matches no live category

export interface IGpthelpVisibilityCtx {
  branchIdStr: string | null; // String(selectedBranch.id) or null
  hoursEnforced: boolean; // effectiveTier > 0 && branch has location tz
  isInsideHours: (ranges: ISimpleHourRange[]) => boolean; // injected closure (testability seam)
}

export interface IGpthelpResolvedVisibility {
  customerVisibleNow: boolean;
  hiddenReasons: IGpthelpResolvedHiddenReason[];
}

// NOTE: an empty hourRanges array is treated as "no restriction" (timeInsideHours
// returns false for an empty array, which would wrongly mean "always hidden").
// Deliberate, documented divergences from the customer app: (a) it would hide an
// entry with hourRanges: [] (no such rows exist in prod); (b) it hides hour-ranged
// categories when the branch has no location timezone, while here hour reasons are
// suppressed entirely in that case (hoursEnforced: false) — fail-open, consistent
// with the customer app's own item-level rule.
const hasHourRestriction = (
  hourRanges: ISimpleHourRange[] | null | undefined
): hourRanges is ISimpleHourRange[] =>
  Array.isArray(hourRanges) && hourRanges.length > 0;

export function categoryHiddenReasons(
  category: ICategory,
  ctx: IGpthelpVisibilityCtx
): IGpthelpHiddenReason[] {
  const reasons: IGpthelpHiddenReason[] = [];

  if (!category.visible) reasons.push('disabled');
  if (
    ctx.branchIdStr !== null &&
    category.hiddenBranchesIds?.includes(ctx.branchIdStr)
  ) {
    reasons.push('hidden_in_branch');
  }
  if (category.posOnly) reasons.push('pos_only');
  if (
    ctx.hoursEnforced &&
    hasHourRestriction(category.hourRanges) &&
    !ctx.isInsideHours(category.hourRanges)
  ) {
    reasons.push('outside_hours');
  }

  return reasons;
}

export function itemHiddenReasons(
  item: IItem,
  ctx: IGpthelpVisibilityCtx
): IGpthelpItemHiddenReason[] {
  const reasons: IGpthelpItemHiddenReason[] = [];

  if (!item.visible) reasons.push('disabled');
  if (
    ctx.branchIdStr !== null &&
    item.hiddenBranchesIds?.includes(ctx.branchIdStr)
  ) {
    reasons.push('hidden_in_branch');
  }
  if (
    ctx.hoursEnforced &&
    hasHourRestriction(item.hourRanges) &&
    !ctx.isInsideHours(item.hourRanges)
  ) {
    reasons.push('outside_hours');
  }

  return reasons;
}

export function resolveItemVisibility(params: {
  item: IItem;
  category: ICategory | null;
  menuOffered: boolean;
  ctx: IGpthelpVisibilityCtx;
}): IGpthelpResolvedVisibility {
  const { item, category, menuOffered, ctx } = params;

  const hiddenReasons: IGpthelpResolvedHiddenReason[] = itemHiddenReasons(
    item,
    ctx
  ).map((reason) => `item_${reason}` as const);

  if (category === null) {
    hiddenReasons.push('category_missing');
  } else {
    hiddenReasons.push(
      ...categoryHiddenReasons(category, ctx).map(
        (reason) => `category_${reason}` as const
      )
    );
    if (!menuOffered) hiddenReasons.push('menu_not_offered');
  }

  return {
    customerVisibleNow: hiddenReasons.length === 0,
    hiddenReasons,
  };
}

export function resolveCategoryVisibility(params: {
  category: ICategory;
  menuOffered: boolean;
  ctx: IGpthelpVisibilityCtx;
}): IGpthelpResolvedVisibility {
  const { category, menuOffered, ctx } = params;

  const hiddenReasons: IGpthelpResolvedHiddenReason[] = categoryHiddenReasons(
    category,
    ctx
  ).map((reason) => `category_${reason}` as const);

  if (!menuOffered) hiddenReasons.push('menu_not_offered');

  return {
    customerVisibleNow: hiddenReasons.length === 0,
    hiddenReasons,
  };
}
